/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import BookingStepList from "../components/booking/steps/BookingStepList";
import SelectStepItem from "../components/booking/SelectStepItem";
import Icon from "../components/ui/Icon";
import { ROUTES } from "../constants/constants";
import { useBooking } from "../hooks/useBooking";
import { useClassSelection } from "../hooks/useClassSelection";
import { useStepManager } from "../hooks/useStepManager";
import { useTranslate } from "../hooks/useTranslate";
import { setFlow, FlowType } from "../store/slices/flowSlice";

const BookingTypeChooser = () => {
  const dispatch = useDispatch();
  const { _t } = useTranslate();
  const { navigateToStep } = useStepManager();
  const { selectedServices, resetBookingRequest } = useBooking();
  const { selectedClass } = useClassSelection();
  const hasServicesSelection = !selectedClass && selectedServices.length > 0;

  useEffect(() => {
    dispatch(setFlow(FlowType.BOOKING));
  }, []);

  const choose = (route, clearsSelection) => {
    if (clearsSelection) resetBookingRequest();
    navigateToStep(route);
  };

  const classesDescription =
    (selectedClass && `${_t("Continue")}: ${selectedClass.name}`) ||
    (hasServicesSelection && _t("Switching clears your services selection")) ||
    _t("Group sessions on a fixed schedule");

  const servicesDescription =
    (hasServicesSelection && `${_t("Continue")}: ${selectedServices.length} ${_t("services")}`) ||
    (selectedClass && _t("Switching clears your class selection")) ||
    _t("Choose the service, provider and time");

  return (
    <Container>
      <NavigationHeader location locationSelectable navigation cart clientLogin />
      <BookingStepList title="What would you like to book?">
        <SelectStepItem
          icon={<Icon.SelectClass size={Icon.SIZE.SM} />}
          label="Classes"
          description={classesDescription}
          descriptionColor={hasServicesSelection ? "error" : undefined}
          onSelect={() => choose(ROUTES.CLASSES, hasServicesSelection)}
        />
        <SelectStepItem
          icon={<Icon.SelectServices size={Icon.SIZE.SM} />}
          label="Services"
          description={servicesDescription}
          descriptionColor={selectedClass ? "error" : undefined}
          onSelect={() => choose(ROUTES.SERVICES_HOME, !!selectedClass)}
        />
      </BookingStepList>
    </Container>
  );
};

export default BookingTypeChooser;
